import createAxiosInstance from "./axios";
import { buildFilterStack } from "../utils/filterUtils";
import {
  CANCELLATION_POLICIES_QUERY,
  ROOMTYPES_QUERY,
  SEASONAL_POLICIES_QUERY,
  TAXES_QUERY,
} from "../constants/graphqlQueries";
import { getApiErrorMessage } from "../utils/apiErrorMessage";
import { stripHiddenRatePlansFromResponse } from "../utils/hiddenRatePlans";

let API_SERVER_PREFIX =
  import.meta.env.VITE_SERVER_DOMAIN + import.meta.env.VITE_SERVER_ENVIRONMENT;

const BE_API_PREFIX = "BE";

const {
  axiosInstance,
  getRequest,
  postRequest,
  putRequest,
  patchRequest,
  getFileRequest,
  postFileRequest,
  postFileUploadRequest,
} = createAxiosInstance(API_SERVER_PREFIX);

// Re-exported for packages that accept an AxiosInstance directly
// (e.g. @hoteltradergit/user-management's UserManagementProvider).
export { axiosInstance };

const propertiesPath = ({ searchText = "", page = 1, limit = 20 } = {}) => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const trimmedSearchText = searchText.trim();
  if (trimmedSearchText) {
    params.append("searchText", trimmedSearchText);
  }
  return `BE/api/v1/properties?${params.toString()}`;
};

export const getProperties = (options) => getRequest(propertiesPath(options));

// Same search on the raw instance: no error toast, for the email deep link's
// silent property lookup during boot (EO-1893).
export const getPropertiesQuietly = (options) =>
  axiosInstance.get(propertiesPath(options));

export const getRecommendations = (propertyId) =>
  getRequest(`BE/api/v1/recommendations?propertyId=${propertyId}`);

export const getRecommendationsV2 = (propertyId) =>
  getRequest(`BE/api/v2/recommendations?propertyId=${propertyId}`);

// One v2 card by id, with the property it belongs to — what an email deep link
// that carries only the recommendationId resolves through (EO-1893). Calls the
// raw instance so a 403/404 reaches the caller with its status and no toast:
// the deep link answers both with the Unauthorized page instead.
export const getRecommendationV2ById = (recommendationId) =>
  axiosInstance.get(
    `BE/api/v2/recommendations/${encodeURIComponent(recommendationId)}`
  );

// Action Centre v2: record what the supplier did with the given cards, which is
// what removes them from the feed. `recommendationIds` are the `recommendationId`
// values from GET /v2/recommendations — ACV2_<CRS>_<THEME>_<YYYYMMDD>.
//
// One endpoint, two outcomes — `action` decides which column is stamped in
// action_centre_card_state: DISMISS ("Maybe Later" / X) writes dismissed_at,
// USED (acted on the CTA) writes used_at. Either hides the card; they are kept
// apart so dismissal rate and conversion stay reportable.
//
// The read filters on both, so without this call the hide is session-only and
// the card returns on the next reload. Idempotent. Per-run: the id ends in the
// run's date, so the next run regenerates the card under a new id.
export const postActionCentreCardAction = ({
  propertyId,
  recommendationIds,
  action,
}) =>
  postRequest(`BE/api/v2/recommendations/action`, {
    propertyId,
    recommendationIds,
    action,
  });

export const postCorridorAction = (payload) =>
  postRequest(`BE/api/v1/recommendations/corridor/action`, payload);

export const postCorridorRequestAction = (payload) =>
  postRequest(`BE/api/v1/recommendations/corridor-request/action`, payload);

export const postCampaignAction = (payload) =>
  postRequest(`BE/api/v1/recommendations/campaign/action`, payload);

export const getDerivedRateplanApplicableRates = (payload) =>
  postRequest(`BE/api/v1/derived-rateplan/get-applicable-rates`, payload);

export const getClientMediaPresignedUrls = (ids, clientId) =>
  postRequest(`BE/api/v1/client-media/presigned-urls`, { ids, clientId });

// BnBerry Action Centre card: single action endpoint (like the other cards).
// OPT_IN records the "Opt-in Now" click + fires the AM Slack alert (backend) and
// flips RECOMMENDATION → PENDING; DISMISS permanently suppresses the card for the
// property so it won't return on reload.
export const bnberryAction = (propertyId, action) =>
  postRequest(`BE/api/v1/recommendations/bnberry/action`, {
    propertyId,
    action,
  });

export const getPromotionsSummary = ({ startDate, endDate, propertyId }) =>
  getRequest(
    `BE/api/v1/promo-codes/summary?startDate=${startDate}&endDate=${endDate}&propertyId=${propertyId}`
  );

export const getPromotions = (pathParams) => {
  return getRequest(`BE/api/v1/promo-codes${pathParams}`);
};

export const searchPromotions = (pathParams) => {
  return getRequest(`BE/api/v1/promo-codes/search${pathParams}`);
};

export const getMarketPromotionById = (promoId) => {
  return getRequest(
    `BE/api/v1/alignment-intelligence-output/promo?promoId=${promoId}`
  );
};

export const getMarketPromoIdByProperty = (propertyId) => {
  return getRequest(
    `BE/api/v1/alignment-intelligence-output/promo-id-by-property?propertyId=${propertyId}`
  );
};

export const updatePromoUsed = (promoId) => {
  return putRequest(
    `BE/api/v1/alignment-intelligence-output/update-has-promo-used?promoId=${promoId}`
  );
};

// Hidden ("do not touch") rate plans are stripped here so every consumer —
// the ARI screens via rateplanSlice and all promotion-creation flows — sees
// the same filtered list. See src/constants/hiddenRatePlans.js (EO-1805).
export const getRatePlansByProperty = (propertyId) => {
  return getRequest(`BE/api/v1/rate-plans/${propertyId}`).then((response) =>
    stripHiddenRatePlansFromResponse(response, "data")
  );
};

/**
 * Search rate plans with pagination, sorting, and filtering
 * @param {Object} params - Search parameters
 * @param {string} params.propertyId - Property ID (required)
 * @param {string} params.search - Search query (optional, defaults to empty string)
 * @param {string} params.sortBy - Field to sort by: "name" or "status" (optional, defaults to "name")
 * @param {string} params.sortOrder - Sort order: "asc" or "desc" (optional, defaults to "asc")
 * @param {number} params.page - Page number (0-based, defaults to 0)
 * @param {number} params.size - Page size (defaults to 10)
 * @param {string} params.clientType - Client type filter: "all", "exclusive", "non_exclusive" (defaults to "all")
 * @returns {Promise<Object>} - API response with rate plans and pagination info
 */
export const searchRatePlans = ({
  propertyId,
  search = "",
  sortBy = "name",
  sortOrder = "asc",
  page = 0,
  size = 10,
  clientType = "all",
}) => {
  // Build params object - only include search if it has a value
  const paramsObj = {
    propertyId,
    sortBy,
    sortOrder,
    page: String(page),
    size: String(size),
    clientType,
  };

  // Only add search parameter if user has entered a search query
  // Keep it empty by default to avoid potential backend issues
  if (search && search.trim()) {
    paramsObj.search = search;
  }

  const params = new URLSearchParams(paramsObj);

  // Hidden ("do not touch") rate plans are stripped from each page so they
  // never reach the rate plan listing (legacy table and derived-rate tree).
  // The server's totalElements/totalPages are left as-is, so a page holding a
  // hidden plan simply renders fewer rows. See src/constants/hiddenRatePlans.js
  // (EO-1805).
  return getRequest(
    `METADATA/v2/metadata/rateplan/search?${params.toString()}`
  ).then((response) => stripHiddenRatePlansFromResponse(response, "content"));
};

export const getPromoRatePlansByProperty = (propertyId) => {
  return getRequest(`BE/api/v1/dpr-rate-plans/${propertyId}`);
};

export const getRoomsByProperty = (propertyId) => {
  return getRequest(`BE/api/v1/room-types/${propertyId}`);
};

export const getPromo = (promoCodeId, propertyId) => {
  return getRequest(
    `BE/api/v1/promo-codes/${promoCodeId}/property/${propertyId}`
  );
};

export const savePromo = (promo, isDraft, calculationType) => {
  // Handle both single promo and array of promos for batch operations
  const promosArray = Array.isArray(promo) ? promo : [promo];

  // Determine promoType - default to "CUSTOM" if not provided
  // For batch operations, use the promoType from the first promo, or default to "CUSTOM"
  const promoType = promosArray[0]?.promoType || "CUSTOM";

  // Build query parameters
  const queryParams = `calculationType=${calculationType}&promoType=${promoType}${isDraft ? "&draft=true" : ""}`;

  return postRequest(`BE/api/v1/promo-codes/batch?${queryParams}`, promosArray);
};

export const createSmartPromo = (
  promo,
  calculationType,
  { suggestionPromoId, promoCategory }
) => {
  const queryParams = `calculationType=${calculationType}&promoType=SMART&suggestionPromoId=${suggestionPromoId}&promoCategory=${promoCategory}`;
  return postRequest(`BE/api/v1/promo-codes?${queryParams}`, promo);
};

export const markPromoRecommendationUsed = ({ propertyId, promoIds, type }) => {
  return postRequest(`BE/api/v1/recommendations/promo/mark-used`, {
    propertyId,
    promoIds,
    type,
  });
};

export const disablePromo = (promoId, propertyId) => {
  return putRequest(
    `BE/api/v1/promo-codes/disable?promoCodeId=${promoId}&propertyId=${propertyId}`,
    {}
  );
};

export const deletePromo = (promoId, propertyId) => {
  return putRequest(
    `BE/api/v1/promo-codes/delete?promoCodeId=${promoId}&propertyId=${propertyId}`,
    {}
  );
};

export const activatePromo = (promoId, propertyId) => {
  return getRequest(
    `BE/api/v1/promo-codes/${promoId}/property/${propertyId}/active`
  );
};

// ── Derived Rates Promo APIs (for pilot hotels) ──

export const getDerivedPromos = (propertyId) =>
  getRequest(
    `METADATA/v2/metadata/derived-rateplan/promo?propertyId=${propertyId}`
  );

/**
 * Persists "Maybe Later" on the promo-expiry card.
 *
 * Takes every recommendationId the card was rendered from, including rows hidden behind the "+ N more"
 * expander — sending only the visible ones leaves the hidden rows to return alone in tomorrow's card.
 */
// propertyId is the authorization subject, not a filter: recommendationId is a global auto-increment,
// so the backend refuses an unscoped dismissal rather than trusting the ids alone.
export const dismissPromoExpiry = (recommendationIds, propertyId) =>
  postRequest(`BE/api/v1/recommendations/promo-expiry/dismiss`, {
    recommendationIds,
    propertyId,
  });

export const getDerivedPromoDetail = (promoId, propertyId) =>
  getRequest(
    `METADATA/v2/metadata/derived-rateplan/promo/${promoId}?propertyId=${propertyId}`
  );

// The three mutations below take a UAL `source` (a UAL_SOURCES value) so the
// backend can emit the PROMO_* activity events. Enable, disable and
// delete are all the same PATCH .../status call, discriminated by `status` —
// the backend derives the action from that, not from the source header.
export const createDerivedPromo = (payload, source) =>
  postRequest(`METADATA/v2/metadata/derived-rateplan/promo`, payload, source);

export const updateDerivedPromo = (promoId, propertyId, payload, source) =>
  putRequest(
    `METADATA/v2/metadata/derived-rateplan/promo/${promoId}?propertyId=${propertyId}`,
    payload,
    source
  );

export const updateDerivedPromoStatus = (promoId, propertyId, status, source) =>
  patchRequest(
    `METADATA/v2/metadata/derived-rateplan/promo/${promoId}/status?propertyId=${propertyId}&status=${status}`,
    {},
    source
  );

export const checkIsPilotProperty = (propertyId) =>
  getRequest(
    `METADATA/v2/metadata/derived-rateplan/pilot-properties/${propertyId}`
  );

// ── Derived Rates APIs (GC-parity, metadata) ──

export const getDerivedRates = (propertyId) =>
  getRequest(`METADATA/v2/metadata/derived-rateplan?propertyId=${propertyId}`);

export const getDerivedRateDetail = (derivedRateId, propertyId) =>
  getRequest(
    `METADATA/v2/metadata/derived-rateplan/${derivedRateId}?propertyId=${propertyId}`
  );

export const getPowerBiEmbedConfig = async () => {
  return await getRequest(`BE/api/v1/power-bi/embed-config`);
};

export const downloadPromotionsInXlsx = (pathParams) =>
  getFileRequest(`BE/api/v1/promo-codes/export${pathParams}`);

export const getStatusBreakdown = (id) => {
  return getRequest(`BE/api/v1/promo-codes/status-breakdown?propertyId=${id}`);
};

export const updateARI = async (payload, source) => {
  return await postRequest("ARI/ari/updateByProduct", payload, source);
};

export const getAri = ({
  propertyId,
  rateplanIds,
  roomtypeIds,
  startDate,
  endDate,
  includeNumberOfRoomsSold = false,
}) => {
  return postRequest(`BE/ari/property`, {
    propertyId,
    rateplanIds,
    roomtypeIds,
    dateRanges: [{ startDate, endDate }],
    includeNumberOfRoomsSold,
  });
};

export const getReservations = (data) => {
  return postRequest(`BE/api/get-reservations`, data);
};

export const getReservationsExport = (data) => {
  return postFileRequest(`BE/api/get-reservations/download`, data);
};

// Per-night payable breakdown keyed by HT confirmation code. The backend
// accepts up to 100 comma-separated codes and echoes every requested code
// back in the response map (empty list when nothing is stored for it).
export const getReservationDailyRates = (htConfirmationCodes) => {
  const params = new URLSearchParams({
    htConfirmationCodes: htConfirmationCodes.join(","),
  });
  return getRequest(`BE/api/reservationDailyRates?${params.toString()}`);
};

export const getDemandClients = () => {
  return getRequest(`BE/api/v1/clients`);
};

export const getDemandClientsByProperty = (propertyId) => {
  return getRequest(`BE/api/get-demand-clients/${propertyId}`);
};

export const repushVcc = (confirmationCode, username, source) => {
  return getRequest(
    `BOOKING_ASYNC/bookings/vcc/repush/${confirmationCode}/user/${username}`,
    source
  );
};

export const updateReservationIds = (data, source) => {
  return postRequest(`BOOKING/admin/modify/crs`, data, source);
};

export const uploadOfflineExcel = (payload, source) =>
  postFileUploadRequest(`BOOKING/admin/offlinePMSUpload`, payload, source);

export const uploadAriByFile = (userId, payload, source) =>
  postFileUploadRequest(
    `ARI/ari-excel/upload?userId=${userId}`,
    payload,
    source
  );

export const downloadAriTemplate = (propertyId) =>
  postFileRequest(`ARI/ari-excel/download-template?propertyId=${propertyId}`);

export const getPromotionsByRateplanId = (propertyId, rateplanId) =>
  getRequest(`ARI/promo/property/${propertyId}/rateplan/${rateplanId}`);

// UAL Read API — activity-log search (GET /api/v1/ual/events) and registry-driven
// drill-down (POST /api/v1/ual/events/{correlation_id}/details). Responses use the
// house AppResponse envelope ({ success, message, data, error }); callers read data.data.
export const searchUalEvents = (queryString = "") =>
  getRequest(`BE/api/v1/ual/events${queryString}`);

// Excel export of every event the current filters match (not just the loaded page).
// Streams a workbook back, so it goes through the file-request path (blob response).
export const exportUalEvents = (queryString = "") =>
  getFileRequest(`BE/api/v1/ual/events/export${queryString}`);

export const getUalEventDetails = (correlationId, payload) =>
  postRequest(
    `BE/api/v1/ual/events/${encodeURIComponent(correlationId)}/details`,
    payload
  );

// Type-ahead user lookup for the User Name / User Email pickers; each result's
// id is submitted as the actor_id search filter.
export const searchUalUsers = (query, limit = 20) =>
  getRequest(
    `BE/api/v1/ual/users?q=${encodeURIComponent(query)}&limit=${limit}`
  );

export const getCancellationPolicies = (propertyId) =>
  postRequest(`METADATA/graphql/metadata/getCancellationPolicies`, {
    query: CANCELLATION_POLICIES_QUERY,
    variables: {
      filters: JSON.stringify({ property_id: String(propertyId) }),
    },
  });

export const getSeasonalPolicies = (propertyId) =>
  postRequest(`METADATA/graphql/metadata/getSeasonalPolicies`, {
    query: SEASONAL_POLICIES_QUERY,
    variables: {
      filters: JSON.stringify({ property_id: String(propertyId) }),
    },
  });

export const getTaxesAndFees = (propertyId) =>
  postRequest(`METADATA/graphql/metadata/getTaxes`, {
    query: TAXES_QUERY,
    variables: {
      filters: JSON.stringify({ property_id: String(propertyId) }),
    },
  });

export const getRoomTypes = (propertyId) =>
  postRequest(`METADATA/graphql/metadata/getRoomTypes`, {
    query: ROOMTYPES_QUERY,
    variables: {
      filters: JSON.stringify({ property_id: String(propertyId) }),
    },
  });

// The user permissions bootstrap call and first-login activation now live in
// @hoteltradergit/user-management's own API client — see
// ./userManagementApiClient.js (kept separate so this widely-imported module
// stays free of a direct dependency on the package).

// Helper function to validate and process search parameters
const validateAndProcessSearchParams = ({
  hotelIds = [],
  dates = [],
  bookDate = null,
  guestAges = "",
  sortBy = "departureDate",
  sortDirection = "asc",
  filters = {},
  filterStack = [],
}) => {
  // Validate required parameters
  if (!hotelIds || hotelIds.length === 0) {
    throw new Error("Hotel IDs are required");
  }

  if (!dates || dates.length === 0) {
    throw new Error("Date ranges are required");
  }

  // Validate and process dates
  const validDates = dates.filter((dateRange) => {
    return (
      dateRange?.checkInDate &&
      dateRange?.checkOutDate &&
      dateRange?.checkInDate <= dateRange.checkOutDate
    );
  });

  if (validDates.length === 0) {
    throw new Error("At least one valid date range is required");
  }

  // Generate filterStack based on the user's filter selection order
  const finalFilterStack = buildFilterStack(filterStack, filters);

  // Prepare request payload
  return {
    hotelIds: hotelIds.filter((id) => Boolean(id)), // Remove empty IDs
    dates: validDates,
    bookDate: bookDate || null,
    guestAges: guestAges || "",
    sortBy: sortBy || "departureDate",
    sortDirection: sortDirection || "asc",
    filters: filters || {},
    filterStack: finalFilterStack,
  };
};

// Helper function to handle API errors
const handleApiError = (error, operation) => {
  if (error.response) {
    const status = error.response.status;
    const message =
      error.response.data?.message ||
      error.response.data?.error ||
      `${operation} request failed`;

    return new Error(`${operation} failed (${status}): ${message}`);
  } else if (error.request) {
    // Request was made but no response received
    return new Error(`${operation} request timed out. Please try again.`);
  } else {
    // Something else happened
    return new Error(`${operation} error: ${error.message || "Unknown error"}`);
  }
};

/**
 * Search for room rates based on specified criteria
 * @param {Object} params - Search parameters
 * @param {Array<string>} params.hotelIds - Array of hotel IDs to search
 * @param {Array<Object>} params.dates - Array of date range objects with checkInDate and checkOutDate
 * @param {string} params.bookDate - Booking creation date (YYYY-MM-DD format)
 * @param {string} params.guestAges - Comma-separated string of guest ages (e.g., "30,30,4,4")
 * @param {number} params.page - Page number (1-based, defaults to 1)
 * @param {string} params.sortBy - Field to sort by (defaults to "departureDate")
 * @param {string} params.sortDirection - Sort direction "asc" or "desc" (defaults to "asc")
 * @param {number} params.pageSize - Number of items per page (defaults to 10)
 * @param {Object} params.filters - Filter criteria (rateplan, roomname, promotion, mealplan, refundable)
 * @param {Array<string>} params.filterStack - Order in which user selected filters (e.g., ["rateplan", "promotion", "roomname"])
 * @returns {Promise<Object>} - API response with room rates and pagination info
 */
export const getSearchResults = async ({
  hotelIds = [],
  dates = [],
  bookDate = null,
  guestAges = "",
  page = 1,
  sortBy = "departureDate",
  sortDirection = "asc",
  pageSize = 10,
  filters = {},
  filterStack = [],
}) => {
  try {
    const basePayload = validateAndProcessSearchParams({
      hotelIds,
      dates,
      bookDate,
      guestAges,
      sortBy,
      sortDirection,
      filters,
      filterStack,
    });

    const payload = {
      ...basePayload,
      page,
      pageSize,
    };

    const response = await postRequest("SEARCH/search", payload);

    // Validate response structure
    if (!response || typeof response !== "object") {
      throw new Error("Invalid response format from search API");
    }

    // Ensure response has expected structure
    const normalizedResponse = {
      status: response.status || 200,
      data: {
        roomRates: response.data?.roomRates || [],
        totalElements: response.data?.totalElements || 0,
        totalPages: response.data?.totalPages || 0,
        page: response.data?.page || 1,
        pageSize: response.data?.pageSize,
        filtersResponse: response.data?.filtersResponse || {
          ratePlans: [],
          roomNames: [],
          mealPlans: [],
          promotions: [],
        },
      },
    };
    return normalizedResponse;
  } catch (error) {
    // EO-1137: the shared axios client throws the raw response body on a 400, so it has no
    // .message for handleApiError to read — the real reason was being replaced with
    // "Search error: Unknown error". getApiErrorMessage exists for this exact shape.
    return new Error(getApiErrorMessage(error, "Failed to fetch data"));
  }
};

/**
 * Download search results as Excel file
 * @param {Object} params - Search parameters for download
 * @param {Array<Object>} params.dates - Array of date range objects with checkInDate and checkOutDate
 * @param {string} params.guestAges - Comma-separated string of guest ages (e.g., "30,30,4,4")
 * @param {string} params.bookDate - Booking creation date (YYYY-MM-DD format)
 * @param {Array<string>} params.columns - Array of column dataKeys to include in export
 * @param {Array<Object>} params.tabs - Array of tab configurations
 * @param {Array<string>} params.tabs[].hotelIds - Array of hotel IDs for this tab
 * @param {Object} params.tabs[].filters - Filter criteria for this tab (rateplan, roomname, promotion, mealplan, refundable)
 * @param {Array<string>} params.tabs[].filterStack - Order in which user selected filters for this tab
 * @param {string} params.tabs[].sortBy - Field to sort by for this tab (defaults to "departureDate")
 * @param {string} params.tabs[].sortDirection - Sort direction "asc" or "desc" for this tab (defaults to "asc")
 * @returns {Promise<Blob>} - Excel file as blob
 */
export const downloadSearchResults = async ({
  dates = [],
  guestAges = "",
  bookDate = null,
  columns = [],
  tabs = [],
}) => {
  try {
    // Validate required parameters
    if (!dates || dates.length === 0) {
      throw new Error("Date ranges are required");
    }

    if (!tabs || tabs.length === 0) {
      throw new Error("At least one tab configuration is required");
    }

    // Validate and process dates
    const validDates = dates.filter((dateRange) => {
      return (
        dateRange?.checkInDate &&
        dateRange?.checkOutDate &&
        dateRange?.checkInDate <= dateRange.checkOutDate
      );
    });

    if (validDates.length === 0) {
      throw new Error("At least one valid date range is required");
    }

    // Process each tab configuration
    const processedTabs = tabs.map((tab, index) => {
      if (!tab.hotelIds || tab.hotelIds.length === 0) {
        throw new Error(`Hotel IDs are required for tab ${index + 1}`);
      }

      // Generate filterStack based on the user's filter selection order for this tab
      const finalFilterStack = buildFilterStack(
        tab.filterStack || [],
        tab.filters || {}
      );

      return {
        hotelIds: tab.hotelIds.filter((id) => Boolean(id)), // Remove empty IDs
        filters: tab.filters || {},
        filterStack: finalFilterStack,
        sortBy: tab.sortBy || "departureDate",
        sortDirection: tab.sortDirection || "asc",
      };
    });

    // Prepare request payload
    const payload = {
      dates: validDates,
      guestAges: guestAges || "",
      bookDate: bookDate || null,
      columns: columns || [],
      tabs: processedTabs,
    };

    const response = await postFileRequest("SEARCH/search/download", payload);
    return response;
  } catch (error) {
    return handleApiError(error, "Download");
  }
};

/**
 * Get property metadata by property ID
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object>} - API response with property metadata
 */
export const getPropertyMetadata = (propertyId) => {
  return getRequest(`METADATA/v2/metadata/property/${propertyId}`);
};

/**
 * Get CRS by property ID
 * @returns {Promise<Object>} - API response with property metadata
 */
export const getCRS = () => {
  return getRequest(`METADATA/v2/metadata/crs`);
};

export const submitContactUs = (payload) =>
  postRequest(
    `${BE_API_PREFIX}/api/v1/property/profile-update-request`,
    payload
  );
